import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authAPI, billingAPI } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, User, CreditCard, Download, Loader2, Check, Zap, XCircle, Plus } from 'lucide-react';

export default function ProfileSettings() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('profile');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [billing, setBilling] = useState(null);

  // Profile form
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [orgRole, setOrgRole] = useState('');
  const [organization, setOrganization] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await authAPI.getMe();
      setUser(res.data);
      setFirstName(res.data.first_name || '');
      setLastName(res.data.last_name || '');
      setOrgRole(res.data.org_role || '');
      setOrganization(res.data.organization || '');

      const billRes = await billingAPI.getPlan();
      setBilling(billRes.data);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    if (!firstName.trim()) { toast.error('First name is required'); return; }
    setSaving(true);
    try {
      await authAPI.updateProfile({ first_name: firstName.trim(), last_name: lastName.trim(), org_role: orgRole.trim(), organization: organization.trim() });
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const startTrial = async () => {
    try {
      await billingAPI.startTrial();
      toast.success('30-day trial activated!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start trial');
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? Your plan will remain active until the billing period ends.')) return;
    try {
      const res = await billingAPI.cancelSubscription();
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    }
  };

  const handlePurchaseAddon = async (addonType) => {
    try {
      const res = await billingAPI.purchaseAddon({ addon_type: addonType, quantity: 1 });
      const { order_id, amount, currency, key_id, prorated_details } = res.data;

      const addonNames = { workspace: 'Extra Workspace', member: 'Extra Member' };
      const options = {
        key: key_id,
        amount,
        currency,
        name: 'Wricoo',
        description: `${addonNames[addonType] || addonType} (prorated $${(amount / 100).toFixed(2)})`,
        order_id,
        handler: async (response) => {
          try {
            await billingAPI.verifyAddonPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success('Add-on activated!');
            loadData();
          } catch (err) {
            toast.error('Payment verification failed');
          }
        },
        prefill: { email: user?.email, name: `${firstName} ${lastName}` },
        theme: { color: '#3B82F6' },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create add-on order');
    }
  };

  const handlePayment = async (planId, billingCycle) => {
    try {
      const res = await billingAPI.createOrder({ plan_id: planId, billing_cycle: billingCycle });
      const { order_id, amount, currency, key_id } = res.data;

      const options = {
        key: key_id,
        amount,
        currency,
        name: 'Wricoo',
        description: `${planId === 'in_house' ? 'In-House Teams' : 'Agency'} Plan (${billingCycle})`,
        order_id,
        handler: async (response) => {
          try {
            await billingAPI.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success('Payment successful! Plan upgraded.');
            loadData();
          } catch (err) {
            toast.error('Payment verification failed');
          }
        },
        prefill: { email: user?.email, name: `${firstName} ${lastName}` },
        theme: { color: '#3B82F6' },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const planConfig = {
    free: { name: 'Forever Free', color: 'text-gray-700', bg: 'bg-gray-100' },
    trial: { name: '30-Day Trial', color: 'text-blue-700', bg: 'bg-blue-100' },
    in_house: { name: 'In-House Teams', color: 'text-indigo-700', bg: 'bg-indigo-100' },
    agency: { name: 'Agency', color: 'text-purple-700', bg: 'bg-purple-100' },
  };

  const currentPlan = planConfig[billing?.user_plan] || planConfig.free;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-lg font-semibold">Account Settings</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-white rounded-lg p-1 border border-gray-200 w-fit">
          <button onClick={() => setTab('profile')} className={`px-4 py-2 rounded-md text-sm font-medium transition ${tab === 'profile' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
            <User className="w-4 h-4 inline mr-1.5" />Profile
          </button>
          <button onClick={() => setTab('billing')} className={`px-4 py-2 rounded-md text-sm font-medium transition ${tab === 'billing' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
            <CreditCard className="w-4 h-4 inline mr-1.5" />Billing
          </button>
        </div>

        {tab === 'profile' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
            <h2 className="text-base font-semibold">Personal Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First name" />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last name" />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input value={user?.email || ''} disabled className="bg-gray-50" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Role in Organization</Label>
                <Input value={orgRole} onChange={e => setOrgRole(e.target.value)} placeholder="e.g. SEO Manager, Content Lead" />
              </div>
              <div>
                <Label>Organization Name</Label>
                <Input value={organization} onChange={e => setOrganization(e.target.value)} placeholder="Your company name" />
              </div>
            </div>
            <Button onClick={saveProfile} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </div>
        )}

        {tab === 'billing' && (
          <div className="space-y-6">
            {/* Current Plan */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-base font-semibold mb-4">Current Plan</h2>
              <div className="flex items-center justify-between">
                <div>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${currentPlan.bg} ${currentPlan.color}`}>
                    {currentPlan.name}
                  </span>
                  {billing?.subscription && (
                    <div className="mt-2 text-sm text-gray-500 space-y-1">
                      <p>Activated: {new Date(billing.subscription.activated_at).toLocaleDateString()}</p>
                      <p>Expires: {new Date(billing.subscription.expires_at).toLocaleDateString()}</p>
                      <p>Billing: {billing.subscription.billing_cycle === 'annual' ? 'Annual' : 'Monthly'}</p>
                      {billing.subscription.status === 'cancelled' && (
                        <p className="text-orange-600 font-medium">Cancelled — active until expiry</p>
                      )}
                    </div>
                  )}
                  {billing?.user_plan === 'trial' && billing?.trial_start && (
                    <p className="mt-2 text-sm text-blue-600">
                      Trial expires: {new Date(new Date(billing.trial_start).getTime() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Limits</p>
                  <p className="text-xs text-gray-400">
                    {billing?.plan?.max_workspaces} workspaces · {billing?.plan?.max_members} members · {billing?.plan?.max_articles >= 999999 ? 'Unlimited' : billing?.plan?.max_articles} articles
                  </p>
                  {billing?.subscription?.status === 'active' && (
                    <Button variant="outline" size="sm" className="mt-3 text-red-600 border-red-200 hover:bg-red-50" onClick={handleCancelSubscription} data-testid="cancel-subscription-btn">
                      <XCircle className="w-3 h-3 mr-1" /> Cancel Subscription
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Upgrade Options — Free users */}
            {(billing?.user_plan === 'free') && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-base font-semibold mb-4">Upgrade Your Plan</h2>
                <div className="grid md:grid-cols-3 gap-4">
                  {/* Trial */}
                  <div className="border border-blue-200 rounded-lg p-4 bg-blue-50/50">
                    <h3 className="font-semibold text-sm">30-Day Trial</h3>
                    <p className="text-xs text-gray-500 mt-1 mb-3">All features, no credit card</p>
                    <Button size="sm" className="w-full" onClick={startTrial}>
                      <Zap className="w-3 h-3 mr-1" />Start Trial
                    </Button>
                  </div>
                  {/* In-House */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-sm">In-House Teams</h3>
                    <p className="text-lg font-bold">$19<span className="text-xs font-normal text-gray-500">/mo</span></p>
                    <p className="text-xs text-gray-400 mb-2">Unlimited articles</p>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => handlePayment('in_house', 'monthly')}>Monthly</Button>
                      <Button size="sm" className="flex-1 text-xs" onClick={() => handlePayment('in_house', 'annual')}>Annual -15%</Button>
                    </div>
                  </div>
                  {/* Agency */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-sm">Agency</h3>
                    <p className="text-lg font-bold">$29<span className="text-xs font-normal text-gray-500">/mo</span></p>
                    <p className="text-xs text-gray-400 mb-2">Unlimited articles</p>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => handlePayment('agency', 'monthly')}>Monthly</Button>
                      <Button size="sm" className="flex-1 text-xs" onClick={() => handlePayment('agency', 'annual')}>Annual -15%</Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Upgrade Options — Trial users */}
            {(billing?.user_plan === 'trial') && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-base font-semibold mb-4">Upgrade to a Paid Plan</h2>
                <p className="text-xs text-gray-500 mb-4">Keep your features after the trial ends.</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-sm">In-House Teams</h3>
                    <p className="text-lg font-bold">$19<span className="text-xs font-normal text-gray-500">/mo</span></p>
                    <p className="text-xs text-gray-400 mb-2">1 workspace · 6 members · Unlimited articles</p>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => handlePayment('in_house', 'monthly')}>Monthly</Button>
                      <Button size="sm" className="flex-1 text-xs" onClick={() => handlePayment('in_house', 'annual')}>Annual -15%</Button>
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-sm">Agency</h3>
                    <p className="text-lg font-bold">$29<span className="text-xs font-normal text-gray-500">/mo</span></p>
                    <p className="text-xs text-gray-400 mb-2">2 workspaces · 10 members · Unlimited articles</p>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => handlePayment('agency', 'monthly')}>Monthly</Button>
                      <Button size="sm" className="flex-1 text-xs" onClick={() => handlePayment('agency', 'annual')}>Annual -15%</Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Add-ons — Paid subscription users */}
            {billing?.subscription?.status === 'active' && ['in_house', 'agency'].includes(billing?.user_plan) && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-base font-semibold mb-4">Add-Ons</h2>
                <p className="text-xs text-gray-500 mb-4">Prorated for your current billing cycle. Charges are calculated based on remaining days.</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-sm">Extra Workspace</h3>
                      <p className="text-xs text-gray-500">$5/mo per workspace</p>
                      {billing?.plan?.addons?.filter(a => a.type === 'workspace').length > 0 && (
                        <p className="text-xs text-blue-600 mt-1">{billing.plan.addons.filter(a => a.type === 'workspace').length} active</p>
                      )}
                    </div>
                    <Button size="sm" onClick={() => handlePurchaseAddon('workspace')} data-testid="buy-workspace-addon">
                      <Plus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-sm">Extra Member</h3>
                      <p className="text-xs text-gray-500">$7/mo per member</p>
                      {billing?.plan?.addons?.filter(a => a.type === 'member').length > 0 && (
                        <p className="text-xs text-blue-600 mt-1">{billing.plan.addons.filter(a => a.type === 'member').length} active</p>
                      )}
                    </div>
                    <Button size="sm" onClick={() => handlePurchaseAddon('member')} data-testid="buy-member-addon">
                      <Plus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Payment History */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-base font-semibold mb-4">Payment History</h2>
              {billing?.payments?.length > 0 ? (
                <div className="space-y-3">
                  {billing.payments.map(p => (
                    <div key={p.payment_id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <div>
                        <p className="text-sm font-medium">Payment #{p.payment_id.slice(-8)}</p>
                        <p className="text-xs text-gray-500">{new Date(p.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold">${(p.amount / 100).toFixed(2)}</p>
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{p.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No payments yet.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
